// SPDX-License-Identifier: Apache-2.0

import { Bytes, ethers, utils, Wallet } from "ethers";
import {
  BlockTag,
  TransactionRequest,
  TransactionResponse,
} from "@ethersproject/abstract-provider";
import { JsonRpcProvider } from "@ethersproject/providers/src.ts/json-rpc-provider";
import { Client, Transaction, Signer } from "@ckb-ccc/core";
import { Listener } from "@ethersproject/abstract-provider/src.ts";
import Address from "../api/types/address";
import { signBlob, signTransaction } from "@stellar/freighter-api";
import { Signature as DERSignature } from "@noble/secp256k1";
import { TransactionWithScriptGroups } from "../api/types/nervosTransactionTypes";
import { convertTxToCcc } from "./TransactionHelper";
import { blake2b } from "@nervosnetwork/ckb-sdk-utils";

export type AddressLike = Address | string;
const ERC20Abi = ["function balanceOf(address owner) view returns (uint256)"];

// The Provider provides an abstraction for the functionality of an Ethereum
// node. Is used for the client instead of requiring a concrete Provider
// implementation.
export default interface Provider {
  getChainID: () => Promise<bigint>;
  signMessage: (msg: Bytes | string) => Promise<string>;
  sendTransaction: (tx: any) => Promise<any>;
  getAddress: () => string;
  // If address is undefined, it uses the own address for the balance queries.
  getETHBalance: (
    blockTag?: BlockTag,
    address?: AddressLike,
  ) => Promise<string>;
  getERC20Balance: (
    tokenAddress: AddressLike,
    address?: AddressLike,
  ) => Promise<string>;
  onBlock: (listener: Listener) => void;
}

export class Web3Provider implements Provider {
  private provider: ethers.providers.Web3Provider;
  private readonly address: string;

  constructor(provider: ethers.providers.Web3Provider, address: string) {
    this.provider = provider;
    this.address = address;
  }

  public getChainID(): Promise<bigint> {
    return this.provider.getNetwork().then((net) => BigInt(net.chainId));
  }

  public signMessage(msg: Bytes | string): Promise<string> {
    const signer = this.provider!.getSigner();
    return signer.signMessage(utils.arrayify(msg));
  }

  public sendTransaction(tx: TransactionRequest): Promise<TransactionResponse> {
    const signer = this.provider!.getSigner();
    return signer.sendTransaction(tx);
  }

  public onBlock(listener: Listener) {
    this.provider.on("block", listener);
  }

  public offBlock(listener: Listener) {
    this.provider.off("block", listener);
  }

  getETHBalance(blockTag?: BlockTag, address?: AddressLike): Promise<string> {
    return this.provider
      .getBalance(address?.toString() || this.address, blockTag)
      .then((bal) => bal.toString());
  }

  getAddress(): string {
    return this.address;
  }

  getERC20Balance(
    tokenAddress: AddressLike,
    address?: AddressLike,
  ): Promise<string> {
    const erc20 = new ethers.Contract(
      tokenAddress.toString(),
      ERC20Abi,
      this.provider,
    );
    return erc20.balanceOf(address?.toString() || this.address);
  }
}

export class JSONRPCProvider implements Provider {
  private readonly provider: JsonRpcProvider;
  private wallet: Wallet;

  constructor(
    secretKey: string,
    ethURL: string,
    networkName: string,
    chainID: number,
  ) {
    this.provider = new ethers.providers.JsonRpcProvider(ethURL, {
      name: networkName,
      chainId: chainID,
    });

    this.wallet = new ethers.Wallet(secretKey, this.provider);
  }

  getChainID(): Promise<bigint> {
    return this.provider.getNetwork().then((net) => {
      return BigInt(net.chainId);
    });
  }

  getProvider(): JsonRpcProvider {
    return this.provider;
  }

  sendTransaction(tx: TransactionRequest): Promise<TransactionResponse> {
    return this.wallet.sendTransaction(tx);
  }

  signMessage(msg: Bytes | string): Promise<string> {
    return this.wallet.signMessage(utils.arrayify(msg));
  }

  onBlock(listener: Listener): void {
    this.provider.on("block", listener);
  }

  getAddress(): string {
    return this.wallet.address;
  }

  getETHBalance(blockTag?: BlockTag, address?: AddressLike): Promise<string> {
    return this.provider
      .getBalance(address?.toString() || this.wallet.address, blockTag)
      .then((bal) => bal.toString());
  }

  getERC20Balance(
    tokenAddress: AddressLike,
    address?: AddressLike,
  ): Promise<string> {
    const erc20 = new ethers.Contract(
      tokenAddress.toString(),
      ERC20Abi,
      this.provider,
    );
    return erc20.balanceOf(address?.toString() || this.wallet.address);
  }
}

export class FreighterProvider {
  private address: string;
  private network: string;

  constructor(network: string, address: string) {
    this.network = network;
    this.address = address;
  }

  public getChainID(): Promise<bigint> {
    return Promise.resolve(BigInt(2));
  }

  sendTransaction(tx: string): Promise<string> {
    if (this.network === "TESTNET") {
      console.log(tx);
      return signTransaction(tx, {
        network: this.network,
        networkPassphrase: "Test SDF Network ; September 2015",
        accountToSign: this.address,
      });
    } else {
      return signTransaction(tx, {
        network: this.network,
        networkPassphrase: "Standalone Network ; February 2017",
        accountToSign: this.address,
      });
    }
  }

  signMessage(msg: Bytes | string): Promise<string> {
    return signBlob(msg.toString(), { accountToSign: this.address });
  }

  getAddress(): string {
    return this.address;
  }
}

/**
 * Converts an Ethereum-style (r,s,v) 65-byte signature into a DER-encoded signature.
 * @param ethSignature Hex string from MetaMask or Rei wallet (65 bytes, r + s + v).
 * @returns DER-encoded signature (hex string with 0x prefix), ~70–73 bytes long.
 */
export async function convertEthSignatureToDER(
  ethSignature: string,
): Promise<string> {
  const PaddedSignatureLength = 73;
  const MarkerByte = 0xff;
  const ZeroByte = 0x00;
  const sigBytes = utils.arrayify(ethSignature);
  if (sigBytes.length !== 65) {
    throw new Error("Signature must be 65 bytes long (Ethereum format).");
  }

  const r = BigInt("0x" + utils.hexlify(sigBytes.slice(0, 32)).slice(2));
  const s = BigInt("0x" + utils.hexlify(sigBytes.slice(32, 64)).slice(2));
  const sig = new DERSignature(r, s);

  const der = sig.toDERRawBytes(); // DER signature (likely 70 bytes)

  if (der.length >= PaddedSignatureLength) {
    throw new Error(
      `signature is too long. Expected at most ${PaddedSignatureLength - 1} bytes, got ${der.length} bytes`,
    );
  }

  // Now pad it manually
  const paddedSig = new Uint8Array(PaddedSignatureLength);
  paddedSig.set(der, 0);
  paddedSig[der.length] = MarkerByte;
  for (let i = der.length + 1; i < PaddedSignatureLength; i++) {
    paddedSig[i] = ZeroByte;
  }

  return utils.hexlify(paddedSig);
}

export class CkbCCCProvider {
  private signer: Signer;
  private client: Client;
  private readonly address: string;
  private lockArg: string | Uint8Array = "";

  constructor(signer: Signer, client: Client, address: string) {
    this.signer = signer;
    this.client = client;
    this.address = address;
  }

  public getAddress(): string {
    return this.address;
  }

  public async getInternalAddress(): Promise<string> {
    return await this.signer.getInternalAddress();
  }

  public async getCodeHash(): Promise<string> {
    const recAddr = await this.signer.getRecommendedAddressObj();
    console.log("recAddr", recAddr);
    return recAddr.script.codeHash.replace(/^0x/, "");
  }

  public async signMessage(msg: Bytes | string): Promise<string> {
    return this.signer.signMessageRaw(utils.arrayify(msg));
  }

  public getChainID(): Promise<bigint> {
    return Promise.resolve(BigInt(3));
  }

  public async signTransaction(
    tx: TransactionWithScriptGroups,
  ): Promise<Transaction> {
    const converted = convertTxToCcc(tx);
    const _tx = Transaction.from(converted);

    try {
      const txSigned = await this.signer.signTransaction(_tx);
      console.log("txSigned", txSigned);
      return txSigned;
    } catch (e) {
      console.error("Failed to send transaction:", e);
      throw e;
    }
  }

  public async signData(data: Uint8Array): Promise<string> {
    const ethSignature = await this.signer.signMessageRaw(data);
    console.log("ethSignature", ethSignature);
    return await convertEthSignatureToDER(ethSignature);
  }
  /**
   * Recovers the SEC1 uncompressed public key from a signature of a known message.
   */
  public async getPublicKey(): Promise<Uint8Array> {
    // const ETHEREUM_SIGNED_MESSAGE_PREFIX = "\x19Ethereum Signed Message:\n32";
    const message = "Get public key";
    const signature = await this.signer.signMessage(message);

    const msgHash = ethers.utils.hashMessage(message);
    const uncompressedKey = ethers.utils.recoverPublicKey(
      msgHash,
      signature.signature,
    );

    const uncompressedBytes = ethers.utils.arrayify(uncompressedKey); // 65 bytes
    const publicKeyX = uncompressedBytes.slice(1, 33);
    const publicKeyY = uncompressedBytes.slice(33, 65);

    const prefix = publicKeyY[31] % 2 === 0 ? 0x02 : 0x03;
    const compressedBytes = [prefix, ...publicKeyX];

    function formatArray(arr: Uint8Array | number[]) {
      return `[${Array.from(arr).join(" ")}]`;
    }

    console.log("Uncompressed public key:", formatArray(uncompressedBytes));
    console.log("Compressed public key:", formatArray(compressedBytes));
    const hash = blake2b(
      32,
      null,
      null,
      new TextEncoder().encode("ckb-default-hash"),
    );
    hash.update(uncompressedBytes);
    const fullHash = hash.digest("hex");
    const lockArg = fullHash.slice(0, 40);
    this.lockArg = lockArg;
    return uncompressedBytes;
  }
}
